import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CalendarClock } from 'lucide-react';
import { api } from '@/lib/api';
import type { Overview } from '@/lib/types';
import { fmtDate, plural } from '@/lib/utils';
import { Badge, Card, CardHeader, Empty, ErrorNote, Loading, PageHeader, Stat } from '@/components/ui';
import { AuditStatusBadge, ModeBadge, ScoreBadge, StoreStatusBadge } from '@/components/badges';

export function OverviewPage() {
  const q = useQuery({ queryKey: ['overview'], queryFn: () => api.get<Overview>('/api/dashboard/overview') });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorNote error={q.error} />;
  const d = q.data!;
  const t = d.totals;
  return (
    <>
      <PageHeader title="Operations overview" subtitle={`${plural(t.stores, 'store')} · official scores from each store's latest completed audit`} />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Average score" value={t.average_score !== null ? `${t.average_score}%` : '—'} sub={`${t.passing} passing · ${t.warning} warning · ${t.failing} failing`} />
        <Stat label="Open action items" value={t.open_action_items} sub={t.overdue_action_items ? `${t.overdue_action_items} overdue` : 'none overdue'} tone={t.overdue_action_items ? 'fail' : 'neutral'} />
        <Stat label="Official audits (30 days)" value={t.official_last_30} sub={`${t.in_progress} in progress`} />
        <Stat label="Practice audits (30 days)" value={t.practice_last_30} tone="practice" />
      </div>

      <div className="mt-5 grid gap-5 xl:grid-cols-3">
        <Card className="xl:col-span-2">
          <CardHeader title="Stores" />
          {d.stores.length === 0 ? <Empty title="No stores yet." /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted-fg uppercase">
                  <tr className="border-b"><th className="px-4 py-2 font-medium">Store</th><th className="px-4 py-2 font-medium">Score</th><th className="px-4 py-2 font-medium">Status</th><th className="px-4 py-2 font-medium">Last official</th><th className="px-4 py-2 font-medium">Open tasks</th></tr>
                </thead>
                <tbody>
                  {[...d.stores].sort((a, b) => (a.metrics.latest_score ?? 101) - (b.metrics.latest_score ?? 101)).map((s) => (
                    <tr key={s.id} className="border-b last:border-0 hover:bg-muted/50">
                      <td className="px-4 py-2.5"><Link to={`/stores/${s.id}`} className="font-medium hover:underline">{s.name}</Link><div className="text-xs text-muted-fg">#{s.store_number}{s.region && ` · ${s.region}`}</div></td>
                      <td className="px-4 py-2.5"><ScoreBadge score={s.metrics.latest_score} /></td>
                      <td className="px-4 py-2.5"><StoreStatusBadge status={s.metrics.status} /></td>
                      <td className="px-4 py-2.5 whitespace-nowrap">{fmtDate(s.metrics.last_audit_date)}</td>
                      <td className="px-4 py-2.5 tabular">{s.metrics.open_action_items}{s.metrics.overdue_action_items > 0 && <Badge tone="fail" className="ml-2">{s.metrics.overdue_action_items} overdue</Badge>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <div className="space-y-5">
          <Card>
            <CardHeader title="Recent audits" subtitle="Last 30 days" />
            {d.recent_audits.length === 0 ? <Empty title="No audits in the last 30 days." /> : (
              <ul className="divide-y">
                {d.recent_audits.map((a) => (
                  <li key={a.id}>
                    <Link to={`/audits/${a.id}`} className="flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-muted/50">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{a.store_name}</p>
                        <p className="truncate text-xs text-muted-fg">{fmtDate(a.audit_date, true)} · {a.auditor_name}</p>
                      </div>
                      <div className="flex flex-none items-center gap-1.5">
                        <ModeBadge mode={a.mode} />
                        {a.status === 'completed' ? <ScoreBadge score={a.score} /> : <AuditStatusBadge status={a.status} locked={false} />}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>
          <Card>
            <CardHeader title="Scheduled" subtitle="Next 14 days" />
            {d.upcoming.length === 0 ? <Empty title="Nothing scheduled." /> : (
              <ul className="divide-y">
                {d.upcoming.map((u) => (
                  <li key={u.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                    <CalendarClock className="size-4 flex-none text-muted-fg" />
                    <div className="min-w-0 flex-1"><Link to={`/stores/${u.store_id}`} className="font-medium hover:underline">{u.store_name}</Link>{u.notes && <p className="truncate text-xs text-muted-fg">{u.notes}</p>}</div>
                    <span className="text-xs whitespace-nowrap text-muted-fg">{fmtDate(u.scheduled_for, true)}{u.scheduled_time && ` ${u.scheduled_time}`}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}
